"use client";
import React, { useState } from "react";
import { CoffeeBean } from "./CoffeeBean";
import { useDesign } from "@/context/DesignContext";
import { getImageUrl } from "@/lib/utils";

type CoffeeProduct = {
  id: number; name: string; temp: "HOT" | "COLD"; price: string;
  tag: string; desc?: string; color?: string; accent?: string;
  image?: string | null; // add image field
};
type MenuProps = { products?: CoffeeProduct[] | string; design?: any };

export default function MENU_COFFEE({ products = [], design: propDesign }: MenuProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;
  const [activeMenu, setActiveMenu] = useState(0);
  const productsArray = Array.isArray(products) ? products : [];
  const filtered = productsArray.filter(c =>
    activeMenu === 0 || (activeMenu === 1 && c.temp === "HOT") || (activeMenu === 2 && c.temp === "COLD")
  );
  if (productsArray.length === 0) return null;

  const bgColor = design?.colors?.background || "#0C0500";
  const primaryColor = design?.colors?.primary || "#C8622A";
  const secondaryColor = design?.colors?.secondary || "#E8A45A";
  const textColor = design?.colors?.text || "#F5E6D3";
  const textLightColor = design?.colors?.textLight || "#BFA76A";
  const filterActiveBg = primaryColor;
  const filterActiveText = bgColor;
  const filterInactiveBorder = `${primaryColor}4D`;
  const filterInactiveText = `${secondaryColor}80`;

  return (
    <section id="menu" className="py-24 relative overflow-hidden">
      <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${bgColor} 0%, #130800 50%, ${bgColor} 100%)` }} />
      <div className="relative z-10 max-w-7xl mx-auto px-8">
        <div className="flex items-end justify-between mb-16">
          <div>
            <div className="font-mono-custom text-xs tracking-[0.5em] uppercase mb-4" style={{ color: primaryColor }}>◈ Our Selection</div>
            <h2 className="font-display text-6xl font-black" style={{ color: textColor }}>The Menu</h2>
          </div>
          <div className="hidden md:flex gap-4">
            {["ALL", "HOT", "COLD"].map((f, i) => (
              <button key={f} onClick={() => setActiveMenu(i)}
                className="font-mono-custom text-xs tracking-widest uppercase px-6 py-2.5 transition-all duration-300"
                style={{
                  background: activeMenu === i ? filterActiveBg : "transparent",
                  color: activeMenu === i ? filterActiveText : filterInactiveText,
                  border: `1px solid ${activeMenu === i ? filterActiveBg : filterInactiveBorder}`,
                }}>{f}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filtered.map((coffee, idx) => (
            <div key={coffee.id} className="card-hover relative p-8 overflow-hidden group"
                 style={{
                   background: `linear-gradient(135deg, ${coffee.color || primaryColor}cc, ${bgColor}E6)`,
                   border: `1px solid ${primaryColor}26`
                 }}>
              {/* Image background if available */}
              {coffee.image ? (
                <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity duration-500">
                  <img
                    src={getImageUrl(coffee.image)}
                    alt={coffee.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="absolute bottom-6 right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <CoffeeBean size={80} opacity={0.15} rotate={coffee.id * 30} />
                </div>
              )}
              <span className="absolute top-6 right-6 font-mono-custom text-xs tracking-widest px-3 py-1 z-10"
                    style={{
                      background: `${coffee.accent || secondaryColor}22`,
                      color: coffee.accent || secondaryColor,
                      border: `1px solid ${coffee.accent || secondaryColor}66`
                    }}>
                {coffee.tag}
              </span>
              <div className="relative z-10">
                <div className="font-mono-custom text-xs tracking-widest uppercase mb-3" style={{ color: `${textLightColor}80` }}>
                  {coffee.temp} · {idx % 2 === 0 ? "Espresso Based" : "Filter"}
                </div>
                <h3 className="font-display text-3xl font-bold mb-3 group-hover:transition-colors duration-300"
                    style={{ color: textColor }}
                    onMouseEnter={(e) => e.currentTarget.style.color = secondaryColor}
                    onMouseLeave={(e) => e.currentTarget.style.color = textColor}>
                  {coffee.name}
                </h3>
                <p className="leading-relaxed mb-8" style={{ fontStyle: "italic", color: `${textLightColor}B3` }}>
                  {coffee.desc || "A carefully crafted coffee."}
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-display text-3xl font-black" style={{ color: coffee.accent || secondaryColor }}>{coffee.price} TND</span>
                  <button className="font-mono-custom text-xs tracking-widest uppercase px-6 py-2.5 transition-all duration-300 hover:scale-105"
                          style={{
                            background: `${coffee.accent || secondaryColor}22`,
                            color: coffee.accent || secondaryColor,
                            border: `1px solid ${coffee.accent || secondaryColor}88`
                          }}>
                    Add to Order
                  </button>
                </div>
              </div>
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                   style={{ background: `linear-gradient(135deg, ${coffee.accent || secondaryColor}14 0%, transparent 60%)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}